<template>
  <div class="broadcast-timeline-vertical">
    <!-- Loading overlay -->
    <b-loading :is-full-page="false" :active="loading" :can-cancel="false">
      <b-icon icon="sync" size="is-large" custom-class="fa-spin"></b-icon>
    </b-loading>

    <!-- Controls -->
    <div class="timeline-controls">
      <div class="date-navigation">
        <b-button size="is-small" @click="previousDay" icon-left="chevron-left">
          Previous Day
        </b-button>
        <div class="date-display">
          <b-icon icon="calendar" size="is-small"></b-icon>
          <span class="date-value">{{ formattedDate }}</span>
        </div>
        <b-button size="is-small" @click="nextDay" icon-left="chevron-right">
          Next Day
        </b-button>
      </div>


      <div class="info-display">
        <span class="info-label">Total Duration:</span>
        <span class="info-value">{{ totalDuration }}</span>
        <span class="info-label" style="margin-left: 20px;">Items:</span>
        <span class="info-value">{{ items.length }}</span>

        <!-- Selection tools - always visible for shortform items -->
        <span class="selection-divider">|</span>
        <b-dropdown aria-role="menu" position="is-bottom-left">
          <template #trigger>
            <b-button size="is-small" icon-left="select-all" icon-right="menu-down">
              Select...
            </b-button>
          </template>
          <b-dropdown-item aria-role="menuitem" @click="showSelectByAssetModal">
            <b-icon icon="content-copy" size="is-small"></b-icon>
            All instances of an asset
          </b-dropdown-item>
          <b-dropdown-item aria-role="menuitem" @click="showSelectByTimeRangeModal">
            <b-icon icon="clock-outline" size="is-small"></b-icon>
            By time range
          </b-dropdown-item>
          <b-dropdown-item aria-role="menuitem" @click="selectAllShortform">
            <b-icon icon="select-all" size="is-small"></b-icon>
            All shortform (TVC/Promo/etc)
          </b-dropdown-item>
          <hr class="dropdown-divider">
          <b-dropdown-item aria-role="menuitem" @click="clearSelection" :disabled="selectedItems.length === 0">
            <b-icon icon="close" size="is-small"></b-icon>
            Clear selection
          </b-dropdown-item>
        </b-dropdown>

        <!-- Copy Day Button -->
        <b-button
          size="is-small"
          icon-left="content-copy"
          @click="showCopyDayModal"
          :disabled="shortformItems.length === 0"
          title="Copy shortform playlist to another day"
        >
          Copy Day
        </b-button>

        <template v-if="selectedItems.length > 0">
          <span class="selection-count">{{ selectedItems.length }} selected</span>
          <b-button
            size="is-small"
            type="is-info"
            icon-left="swap-horizontal"
            @click="startReplaceMode"
            :class="{ 'is-active': replaceMode }"
          >
            {{ replaceMode ? 'Select replacement...' : 'Replace' }}
          </b-button>
          <b-button
            size="is-small"
            type="is-danger"
            icon-left="delete"
            @click="deleteSelectedItems"
          >
            Delete
          </b-button>
          <b-button
            size="is-small"
            @click="clearSelection"
          >
            Clear
          </b-button>
        </template>
      </div>

      <!-- Select by Asset Modal -->
      <b-modal :active.sync="isSelectByAssetModalActive" has-modal-card>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Select All Instances of Asset</p>
            <button class="delete" aria-label="close" @click="isSelectByAssetModalActive = false"></button>
          </header>
          <section class="modal-card-body">
            <p class="mb-3">Choose an asset to select all its instances in today's schedule:</p>
            <div class="asset-list">
              <div
                v-for="asset in uniqueShortformAssets"
                :key="asset.media_id"
                class="asset-option"
                @click="selectAllInstancesOf(asset.media_id)"
              >
                <span class="asset-icon">{{ getMediaIcon(asset.media_type) }}</span>
                <span class="asset-title">{{ asset.title }}</span>
                <span class="asset-count">{{ asset.count }} instance{{ asset.count > 1 ? 's' : '' }}</span>
              </div>
            </div>
          </section>
        </div>
      </b-modal>

      <!-- Select by Time Range Modal -->
      <b-modal :active.sync="isSelectByTimeRangeModalActive" has-modal-card>
        <div class="modal-card">
          <header class="modal-card-head">
            <p class="modal-card-title">Select by Time Range</p>
            <button class="delete" aria-label="close" @click="isSelectByTimeRangeModalActive = false"></button>
          </header>
          <section class="modal-card-body">
            <div class="columns">
              <div class="column">
                <b-field label="From">
                  <b-timepicker
                    v-model="timeRangeStart"
                    placeholder="Start time"
                    hour-format="24"
                    :increment-minutes="30"
                  ></b-timepicker>
                </b-field>
              </div>
              <div class="column">
                <b-field label="To">
                  <b-timepicker
                    v-model="timeRangeEnd"
                    placeholder="End time"
                    hour-format="24"
                    :increment-minutes="30"
                  ></b-timepicker>
                </b-field>
              </div>
            </div>
            <div class="quick-ranges">
              <b-button size="is-small" @click="setTimeRange(6, 0, 12, 0)">Morning (06:00-12:00)</b-button>
              <b-button size="is-small" @click="setTimeRange(12, 0, 18, 0)">Afternoon (12:00-18:00)</b-button>
              <b-button size="is-small" @click="setTimeRange(18, 0, 21, 0)">Prime (18:00-21:00)</b-button>
              <b-button size="is-small" @click="setTimeRange(21, 0, 24, 0)">Late (21:00-00:00)</b-button>
            </div>
            <p class="mt-3 has-text-grey">
              Will select: <strong>{{ getTimeRangePreviewCount() }}</strong> shortform items
            </p>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="isSelectByTimeRangeModalActive = false">Cancel</b-button>
            <b-button type="is-primary" @click="selectByTimeRange">Select Items</b-button>
          </footer>
        </div>
      </b-modal>

      <!-- Copy Day Modal -->
      <b-modal :active.sync="isCopyDayModalActive" has-modal-card>
        <div class="modal-card" style="max-width: 500px;">
          <header class="modal-card-head">
            <p class="modal-card-title">Copy Shortform Playlist</p>
            <button class="delete" aria-label="close" @click="isCopyDayModalActive = false"></button>
          </header>
          <section class="modal-card-body">
            <p class="mb-4">
              Copy <strong>{{ shortformItems.length }}</strong> shortform items from
              <strong>{{ formattedDate }}</strong> to another day.
            </p>

            <b-field label="Destination Date">
              <b-datepicker
                v-model="copyDestinationDate"
                placeholder="Select destination date"
                icon="calendar-today"
                :min-date="minCopyDate"
                trap-focus
              ></b-datepicker>
            </b-field>

            <div class="quick-date-buttons mt-3 mb-4">
              <b-button size="is-small" @click="setCopyDate(7)">+1 Week</b-button>
              <b-button size="is-small" @click="setCopyDate(14)">+2 Weeks</b-button>
              <b-button size="is-small" @click="setCopyDate(1)">Tomorrow</b-button>
            </div>

            <b-message type="is-info" size="is-small">
              <strong>How copying works:</strong><br>
              Shortform items are mapped by their position relative to shows (e.g., "after Break 1 of the 18:00 show").
              If the destination day has different show structures, items will be placed at the closest matching position.
            </b-message>

            <b-message v-if="copyDestinationDate" type="is-warning" size="is-small">
              <strong>Note:</strong> Any existing shortform items on the destination day will be preserved.
              New items will be added based on position mapping.
            </b-message>
          </section>
          <footer class="modal-card-foot">
            <b-button @click="isCopyDayModalActive = false">Cancel</b-button>
            <b-button
              type="is-primary"
              :disabled="!copyDestinationDate"
              :loading="copyingDay"
              @click="executeCopyDay"
            >
              Copy Playlist
            </b-button>
          </footer>
        </div>
      </b-modal>
    </div>

    <!-- Timeline Container (Vertical time, Horizontal items) -->
    <div class="timeline-container" ref="container" @scroll="handleScroll">
      <div class="timeline-content" :style="{ height: timelineHeight + 'px' }">
        <!-- Time axis (left side, vertical) - shows time for each item -->
        <div class="time-axis">
          <div
            v-for="(item, index) in displayItems"
            :key="`time-${index}`"
            class="time-marker"
            :class="{ 'time-shifted': item.isShiftedByDrag }"
            :style="{ top: (index * 65) + 'px' }"
          >
            <div class="time-label" :class="{ 'preview-time': item.isShiftedByDrag }">{{ item.timeLabel }}</div>
            <div class="time-line"></div>
          </div>
        </div>

        <!-- Items track (horizontal rows at each time position) -->
        <div
          class="timeline-track"
          @dragover.prevent="onDragOver"
          @drop="onDrop"
        >
          <div
            v-for="(item, index) in displayItems"
            :key="item.id || `item-${index}`"
            class="timeline-item"
            :class="getItemClass(item)"
            :style="getItemStyle(item, index)"
            :data-index="index"
            :draggable="!item.isPlaceholder && !item.isGap && !item.isGhostBumper && item.item_type === 'media'"
            @click="(item.isPlaceholder || item.isGhostBumper) ? null : $emit('item-click', item)"
            @dragstart="onItemDragStart($event, item)"
            @dragend="onItemDragEnd"
          >
            <!-- Ghost auto-bumper / PG slate (channel-config driven, not stored in DB) -->
            <div class="item-content ghost-bumper-content" v-if="item.isGhostBumper">
              <div class="ghost-icon">{{ item.ghostKind === 'pgslate' ? '🎬' : (item.ghostKind === 'in' ? '▶️' : '⏸️') }}</div>
              <div class="ghost-title">{{ item.title }}</div>
              <div class="ghost-meta">auto-inserted by channel config</div>
            </div>

            <!-- Gap indicator (Available Time or Overrun) -->
            <div class="item-content gap-content" v-else-if="item.isGap">
              <div class="gap-info">
                <!-- Overrun display (red warning) -->
                <template v-if="item.isOverrun">
                  <div class="gap-label overrun-label">⚠️ OVERRUN</div>
                  <div class="gap-duration overrun-duration">{{ formatDuration(item.overrunSeconds) }}</div>
                  <div class="overrun-warning">
                    Items exceed available time before next locked show
                  </div>
                </template>
                <!-- Normal available time display -->
                <template v-else>
                  <div class="gap-label">⏱️ Available Time</div>
                  <div class="gap-duration">{{ formatDuration(item.duration_seconds) }}</div>
                  <div class="gap-next-show" v-if="item.timeToNextLocked">
                    {{ formatDuration(item.timeToNextLocked) }} to next locked show
                    <span v-if="item.nextLockedShowTitle" class="next-show-title">
                      ({{ item.nextLockedShowTitle }})
                    </span>
                  </div>
                </template>
              </div>
            </div>

            <!-- Regular item content -->
            <div class="item-content" v-else-if="!item.isPlaceholder">
              <template v-if="item.item_type === 'segment'">
                <div class="item-title">{{ item.media_title || item.title }}</div>
                <div class="item-meta">
                  <span v-if="item.season_number">S{{ item.season_number }}</span>
                  <span v-if="item.episode_number">E{{ item.episode_number }}</span>
                  <span v-if="item.segment_number"> • Seg {{ item.segment_number }}</span>
                </div>
                <div class="item-duration">{{ formatDuration(item.duration_seconds) }}</div>
              </template>
              <template v-else-if="item.item_type === 'break'">
                <div class="item-title">📺 {{ item.title }}</div>
                <div class="item-duration">{{ formatDuration(item.duration_seconds) }}</div>
              </template>
              <template v-else>
                <b-checkbox
                  class="item-checkbox"
                  :value="isItemSelected(item)"
                  @input="toggleItemSelection(item, $event)"
                  @click.native.stop
                ></b-checkbox>
                <div class="item-title">
                  <span
                    v-if="item.item_type === 'media'"
                    class="tx-status-dot-inline"
                    :class="isMediaTxReady(item) ? 'tx-ready' : 'tx-missing'"
                    :title="isMediaTxReady(item) ? 'TX file present' : 'No TX file linked'"
                  ></span>
                  {{ getMediaIcon(item.media_type) }} {{ item.title }}
                </div>
                <div class="item-duration">{{ formatDuration(item.duration_seconds) }}</div>
                <button
                  class="delete-item-btn"
                  @click.stop="deleteItem(item)"
                  title="Remove this item"
                >
                  ×
                </button>
              </template>
            </div>

            <!-- Placeholder content -->
            <div class="item-content placeholder-content" v-else>
              <div class="item-title" style="opacity: 0.4;">Empty</div>
            </div>
          </div>

          <!-- Drop indicator -->
          <div
            v-if="(isDragging || isDraggingInternal) && dropIndex !== null"
            class="drop-indicator"
            :style="{ top: (dropIndex * 65) + 'px' }"
          >
            <div class="drop-indicator-line"></div>
            <div class="drop-indicator-label">Drop here</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { isMediaTxReady } from '@/utils/mediaStatus'

export default {
  name: 'BroadcastTimeline',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => []
    },
    isDragging: {
      type: Boolean,
      default: false
    },
    draggingItemDuration: {
      type: Number,
      default: 0 // Duration in seconds of item being dragged from media library
    },
    isStale: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    },
    currentDate: {
      type: Date,
      default: () => new Date()
    },
    channel: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      dropIndex: null, // Track where item will be dropped
      draggingItem: null, // Track which timeline item is being dragged
      isDraggingInternal: false, // Track when dragging items within timeline
      selectedItemIds: [], // Track selected item schedule_ids for bulk operations
      replaceMode: false, // When true, next media library selection will replace selected items
      // Selection modals
      isSelectByAssetModalActive: false,
      isSelectByTimeRangeModalActive: false,
      timeRangeStart: null,
      timeRangeEnd: null,
      // Copy day modal
      isCopyDayModalActive: false,
      copyDestinationDate: null,
      copyingDay: false,
      // Drag preview state for optimistic UI updates
      dragPreviewDuration: 0, // Duration of item being dragged (in seconds)
      dragPreviewTitle: '' // Title of item being dragged
    }
  },
  computed: {
    /**
     * Channel ad-bumper + PG-slate auto-insertion config, read DYNAMICALLY
     * from the channel row. Durations vary per tenant (e.g., 6s for 1GOSPEL,
     * 8s for Moja 99, 10s for Moja Love) — never hardcode.
     *
     * Mirrors the export-time logic in api/exportCinegy.js:287-316 — bumpers
     * wrap every segment block (Bumper-In before, Bumper-Out after). PG Slate
     * replaces the Bumper-In on Seg 1 if enabled. The bumpers eat time from
     * the schedule slot and from any AVAILABLE TIME seam adjacent to a segment.
     */
    bumperConfig() {
      const c = this.channel || {}
      const truthy = v => v !== false && v !== 0 && v !== '0'
      // pg_slate_skip_ratings is a JSON column — knex may return it parsed (array)
      // or as a raw JSON string. Normalise both to an uppercase array. Mirrors
      // parseSlateSkipRatings() in api/exportCinegy.js so day-view ghost rows match
      // what the export actually emits for FAMILY-rated shows on channels that
      // don't host the slate file (e.g. Media24).
      let skipRatings = c.pg_slate_skip_ratings
      if (typeof skipRatings === 'string') {
        try { skipRatings = JSON.parse(skipRatings) } catch (e) { skipRatings = [] }
      }
      if (!Array.isArray(skipRatings)) skipRatings = []
      return {
        bumperEnabled: truthy(c.auto_insert_bumpers) && !!c.ad_bumper_path,
        bumperDuration: parseInt(c.ad_bumper_duration, 10) || 10,
        pgSlateEnabled: truthy(c.auto_insert_pg_slates) && !!c.pg_slate_folder,
        pgSlateDuration: parseInt(c.pg_slate_duration, 10) || 10,
        pgSlateSkipRatings: skipRatings.map(r => String(r).toUpperCase().trim()).filter(Boolean)
      }
    },
    displayItems() {
      // Create a full 24-hour timeline with actual items and gap indicators
      const result = []
      const dayStart = new Date(this.currentDate)
      dayStart.setHours(0, 0, 0, 0)

      // If no items, create placeholder items for each hour
      if (!this.items || this.items.length === 0) {
        for (let hour = 0; hour < 24; hour++) {
          const time = new Date(dayStart)
          time.setHours(hour)
          result.push({
            isPlaceholder: true,
            timeLabel: this.formatItemTime(time.toISOString()),
            start: time.toISOString()
          })
        }
        return result
      }

      // Sort items by start time
      const sortedItems = [...this.items].sort((a, b) => new Date(a.start) - new Date(b.start))

      // Find locked show segments (item_type === 'segment' and segment_number === 1)
      const lockedShowStarts = sortedItems.filter(item =>
        item.item_type === 'segment' && item.segment_number === 1
      )

      // OPTIMISTIC UI: Calculate time offset for items AFTER the drop position during drag
      // This gives visual feedback of how times will shift when item is dropped
      // Use prop duration for external drags (from media library), or local state for internal moves
      const previewDuration = this.isDragging ? this.draggingItemDuration : this.dragPreviewDuration
      const isDraggingWithPreview = (this.isDragging || this.isDraggingInternal) &&
        this.dropIndex !== null &&
        previewDuration > 0

      const bc = this.bumperConfig

      // PLAYOUT-CLOCK MODEL (Option B):
      // Walk items in time order accumulating a "playout clock" that advances
      // by bumper durations + content. At each is_locked anchor (Seg 1 of show,
      // or locked-tail break), reset the clock to the DB anchor time. Between
      // anchors, items + their bumpers flow naturally. This mirrors what
      // Cinegy actually plays after the MCRList import (since locked breaks
      // and Seg 1s are the only items the export anchors with start=).
      //
      // Day view becomes playout-accurate. DB rows untouched. Exports untouched.
      let playoutClock = null

      // Add items and calculate gaps between different schedule blocks
      sortedItems.forEach((item, index) => {
        // Determine the playout-clock start for THIS item.
        // Locked items (Seg 1, locked break) anchor the clock to their DB time.
        // Non-locked items use the accumulated clock (or DB time if first).
        const isLockedAnchor = item.is_locked && (item.item_type === 'break' || item.segment_number === 1)
        if (isLockedAnchor || playoutClock === null) {
          playoutClock = new Date(item.start)
        }
        // playoutClock now equals where THIS item's first sub-element starts in
        // Cinegy. For a segment with auto-bumper, the Bumper-In/PG-Slate occupies
        // the first slice; the segment content starts after it. For breaks and
        // standalone media, the item itself starts at playoutClock.

        // GHOST PRE-ROW for segments: leading Bumper-In or PG Slate at playoutClock.
        // Mirrors api/exportCinegy.js:287-316. Durations from channel config.
        // Per-rating skip: when the item's age_rating is in pgSlateSkipRatings the
        // slate is skipped and the bumper-in takes its place — same fallthrough
        // the export does, so day-view stays playout-accurate for FAMILY shows on
        // channels that don't host the slate file.
        if (bc.bumperEnabled && item.item_type === 'segment') {
          const isSeg1 = item.segment_number === 1
          const itemRating = String(item.age_rating || '').toUpperCase()
          const slateSkippedForRating = itemRating && bc.pgSlateSkipRatings.includes(itemRating)
          const useSlate = isSeg1 && bc.pgSlateEnabled && !slateSkippedForRating
          const ghostDur = useSlate ? bc.pgSlateDuration : bc.bumperDuration
          const ghostStart = new Date(playoutClock)
          const ghostEnd = new Date(playoutClock.getTime() + ghostDur * 1000)
          result.push({
            isGhostBumper: true,
            ghostKind: useSlate ? 'pgslate' : 'in',
            title: useSlate ? `PG Slate (${ghostDur}s)` : `Auto Bumper In (${ghostDur}s)`,
            timeLabel: this.formatItemTime(ghostStart.toISOString()),
            start: ghostStart.toISOString(),
            end: ghostEnd.toISOString(),
            duration_seconds: ghostDur
          })
          playoutClock = ghostEnd
        }

        // Calculate optimistic time label during drag (uses raw DB time for drag preview,
        // not playout clock, since the drag math currently lives in DB-time space).
        const itemContentStart = new Date(playoutClock)
        let displayTimeLabel = this.formatItemTime(itemContentStart.toISOString())
        let isShiftedByDrag = false

        if (isDraggingWithPreview) {
          const itemPositionInResult = result.filter(r => !r.isGap && !r.isPlaceholder && !r.isGhostBumper).length
          if (itemPositionInResult >= this.dropIndex) {
            const isLocked = item.is_locked && item.segment_number === 1
            if (!isLocked) {
              const shiftedStart = new Date(itemContentStart.getTime() + (previewDuration * 1000))
              displayTimeLabel = this.formatItemTime(shiftedStart.toISOString())
              isShiftedByDrag = true
            }
          }
        }

        // Push the actual item row. timeLabel uses playout clock, not DB time.
        result.push({
          ...item,
          timeLabel: displayTimeLabel,
          playoutStart: itemContentStart.toISOString(),
          isShiftedByDrag
        })

        // Advance clock by the item's content duration (use DB duration as
        // source of truth — the schedule row tells us how long the content is).
        const contentDurMs = (new Date(item.end) - new Date(item.start))
        playoutClock = new Date(playoutClock.getTime() + contentDurMs)

        // GHOST POST-ROW for segments: trailing Bumper-Out at playoutClock.
        // Mirrors api/exportCinegy.js:314-316.
        if (bc.bumperEnabled && item.item_type === 'segment') {
          const ghostStart = new Date(playoutClock)
          const ghostEnd = new Date(playoutClock.getTime() + bc.bumperDuration * 1000)
          result.push({
            isGhostBumper: true,
            ghostKind: 'out',
            title: `Auto Bumper Out (${bc.bumperDuration}s)`,
            timeLabel: this.formatItemTime(ghostStart.toISOString()),
            start: ghostStart.toISOString(),
            end: ghostEnd.toISOString(),
            duration_seconds: bc.bumperDuration
          })
          playoutClock = ghostEnd
        }

        const nextItem = sortedItems[index + 1]

        if (nextItem) {
          // AVAILABLE TIME = time between current playoutClock (post-bumper-out)
          // and next item's start. For locked-anchor next items, that's the
          // anchor target. For non-locked items the gap collapses in Cinegy
          // (no start= attribute) so we don't show it as AVAILABLE TIME — the
          // existing shouldShowGap guard handles that.
          const gapSeconds = (new Date(nextItem.start) - playoutClock) / 1000

          // IMPORTANT: Show "Available Time" gaps ONLY:
          // - Between different parent shows (end of last segment/break of one show -> start of Seg 1 of next show)
          // - This represents the total available time before the next locked segment 1
          //
          // DO NOT show gaps:
          // - Between segments and breaks within the same show
          // - After commercial breaks within the same show
          //
          // User CAN still DROP/INSERT at those positions, but no visual gap indicator

          // Check if items belong to different parent shows
          const currentParent = item.parent_schedule_id || item.schedule_id
          const nextParent = nextItem.parent_schedule_id || nextItem.schedule_id
          const isDifferentShow = currentParent !== nextParent

          // Show gap/overrun when:
          //   CASE 1 (existing — between shows): Different parent shows AND next item
          //     is segment 1 of next show (locked start). Pre-existing AVAILABLE TIME
          //     behaviour, NOT changed by this edit.
          //   CASE 2 (new — within a show using lock_to_slot_end commercials): Next
          //     item is a locked break. Locked breaks only exist when a channel
          //     template entry has lock_to_slot_end:true (Issue 18b feature, 2026-05-02).
          //     Pre-feature data has no locked breaks, so this branch is a no-op for
          //     any schedule that doesn't use the lock feature.
          //   In both cases the gap must be >= 1s or an overrun (gap < 0).
          const isNextItemLockedSegment1 = nextItem.is_locked && nextItem.segment_number === 1
          const isNextItemLockedBreak = nextItem.is_locked && nextItem.item_type === 'break'
          const hasAvailableTime = gapSeconds >= 1
          const hasOverrun = gapSeconds < 0
          const shouldShowGap =
            ((isDifferentShow && isNextItemLockedSegment1) || isNextItemLockedBreak) &&
            (hasAvailableTime || hasOverrun)

          // Debug logging
          if (gapSeconds !== 0 || isDifferentShow) {
            console.log(`GAP CHECK: "${item.title}" (Parent:${currentParent}, Seg:${item.segment_number}) -> "${nextItem.title}" (Parent:${nextParent}, Seg:${nextItem.segment_number}, Type:${nextItem.item_type}, Locked:${nextItem.is_locked}) | Gap:${gapSeconds}s | DiffShow:${isDifferentShow} | LockedSeg1:${isNextItemLockedSegment1} | LockedBreak:${isNextItemLockedBreak} | ShowGap:${shouldShowGap} | Overrun:${hasOverrun}`)
          }

          if (shouldShowGap) {
            // Find the next locked show segment after this gap.
            // Use playoutClock (post-bumper position) rather than DB itemEnd
            // so the gap reflects actual remaining time after bumper consumption.
            const nextLockedShow = lockedShowStarts.find(locked =>
              new Date(locked.start) > playoutClock
            )

            const timeToNextLocked = nextLockedShow
              ? (new Date(nextLockedShow.start) - playoutClock) / 1000
              : null

            result.push({
              isGap: true,
              isOverrun: hasOverrun,
              overrunSeconds: hasOverrun ? Math.abs(gapSeconds) : 0,
              timeLabel: this.formatItemTime(playoutClock.toISOString()),
              start: playoutClock.toISOString(),
              end: nextItem.start,
              duration_seconds: hasOverrun ? 0 : gapSeconds, // Show 0 for overruns
              timeToNextLocked: timeToNextLocked,
              nextLockedShowTitle: nextLockedShow ? nextLockedShow.media_title : null
            })
          }
        }
      })

      return result
    },

    timelineHeight() {
      // Calculate height based on number of display items (fixed 60px height + 5px spacing)
      const itemsHeight = this.displayItems.length * 65 + 100
      return itemsHeight
    },
    totalDuration() {
      const totalSeconds = this.items.reduce((sum, item) => sum + (item.duration_seconds || 0), 0)
      return this.formatDuration(totalSeconds)
    },
    formattedDate() {
      if (!this.currentDate) return ''
      const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }
      return this.currentDate.toLocaleDateString('en-US', options)
    },
    selectedItems() {
      // Return full item objects for selected items
      return this.items.filter(item =>
        this.selectedItemIds.includes(item.schedule_id)
      )
    },
    shortformItems() {
      // Get only shortform items (TVC, Promo, Filler, GFX) - these are selectable
      const shortformTypes = ['tvc', 'promo', 'filler', 'gfx']
      return this.items.filter(item =>
        item.media_type && shortformTypes.includes(item.media_type.toLowerCase())
      )
    },
    uniqueShortformAssets() {
      // Get unique assets with instance counts
      const assetMap = new Map()
      this.shortformItems.forEach(item => {
        if (item.media_id) {
          if (assetMap.has(item.media_id)) {
            assetMap.get(item.media_id).count++
          } else {
            assetMap.set(item.media_id, {
              media_id: item.media_id,
              title: item.title,
              media_type: item.media_type,
              count: 1
            })
          }
        }
      })
      // Sort by count (most instances first)
      return Array.from(assetMap.values()).sort((a, b) => b.count - a.count)
    },
    minCopyDate() {
      // Don't allow copying to the same day - minimum is tomorrow
      const tomorrow = new Date(this.currentDate)
      tomorrow.setDate(tomorrow.getDate() + 1)
      tomorrow.setHours(0, 0, 0, 0)
      return tomorrow
    }
  },
  watch: {
    items(newItems) {
      console.log('Timeline items updated:', newItems.length)
      // Clean up selection - remove any selected IDs that no longer exist
      const validIds = newItems.map(item => item.schedule_id)
      this.selectedItemIds = this.selectedItemIds.filter(id => validIds.includes(id))
    },
    isDragging(val) {
      if (!val) {
        this.dropIndex = null
      }
    },
    currentDate() {
      // Clear selection when date changes
      this.selectedItemIds = []
    }
  },
  mounted() {
    // Component mounted
  },
  methods: {
    isMediaTxReady,
    /**
     * Check if a given time falls within the channel's commercial break window
     * Uses the channel's commercials_start_time and commercials_end_time configuration
     * Handles midnight crossover cases (e.g., 18:00-06:00)
     */
    isWithinCommercialWindow(dateString) {
      if (!this.channel || !this.channel.commercials_start_time || !this.channel.commercials_end_time) {
        // If no commercial window configured, assume all times are valid
        return true
      }

      const checkTime = new Date(dateString)
      const checkHour = checkTime.getHours()
      const checkMinutes = checkTime.getMinutes()
      const checkTimeDecimal = checkHour + (checkMinutes / 60)

      // Parse TIME string or Date - handle "HH:mm:ss" format from TIME columns
      const parseTimeToDecimal = (timeValue) => {
        if (typeof timeValue === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(timeValue)) {
          const parts = timeValue.split(':')
          return parseInt(parts[0], 10) + (parseInt(parts[1], 10) / 60)
        }
        const d = new Date(timeValue)
        return d.getHours() + (d.getMinutes() / 60)
      }

      const startTimeDecimal = parseTimeToDecimal(this.channel.commercials_start_time)
      const endTimeDecimal = parseTimeToDecimal(this.channel.commercials_end_time)

      // Check if time is within window (handle midnight crossover)
      let isWithinWindow
      if (startTimeDecimal <= endTimeDecimal) {
        // Normal case: 06:00-18:00
        isWithinWindow = checkTimeDecimal >= startTimeDecimal && checkTimeDecimal <= endTimeDecimal
      } else {
        // Midnight crossover: 18:00-06:00 (or 06:00-00:00 which is 06:00-24:00)
        isWithinWindow = checkTimeDecimal >= startTimeDecimal || checkTimeDecimal <= endTimeDecimal
      }

      return isWithinWindow
    },

    formatTimeSlot(slot) {
      const hours = Math.floor(slot)
      const minutes = (slot % 1) * 60
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`
    },

    formatItemTime(dateString) {
      const date = new Date(dateString)
      const hours = date.getHours()
      const minutes = date.getMinutes()
      const seconds = date.getSeconds()
      return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    },

    formatDuration(seconds) {
      if (!seconds) return '0:00'
      const hours = Math.floor(seconds / 3600)
      const mins = Math.floor((seconds % 3600) / 60)
      const secs = seconds % 60

      if (hours > 0) {
        return `${hours}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
      }
      return `${mins}:${String(secs).padStart(2, '0')}`
    },

    getItemClass(item) {
      if (item.isGhostBumper) {
        return `item-ghost-bumper item-ghost-${item.ghostKind}`
      }
      if (item.isGap) {
        // Use different class for overrun (red) vs available time (yellow)
        return item.isOverrun ? 'item-gap item-overrun' : 'item-gap'
      }
      const type = item.item_type || item.media_type || 'default'
      return `item-${type}`
    },

    getItemStyle(item, index) {
      // Fixed height layout - all items are 60px with 5px spacing
      const fixedHeight = 60
      const cumulativeTop = index * 65

      return {
        top: `${cumulativeTop}px`,
        height: `${fixedHeight}px`,
        left: '10px',
        right: '10px',
        opacity: item.isPlaceholder ? 0.3 : 1
      }
    },

    onDragOver(event) {
      event.preventDefault()

      // Calculate which index the mouse is over (fixed 65px row height)
      const rect = event.currentTarget.getBoundingClientRect()
      const y = event.clientY - rect.top + event.currentTarget.scrollTop
      let index = Math.floor(y / 65)
      index = Math.min(Math.max(index, 0), this.displayItems.length)

      // Snap past Issue 43 ghost bumper rows so the drop indicator lands
      // between REAL schedule items, not between ghosts (which carry no DB row
      // and would mislead the user about where the filler will actually go).
      // Snap direction follows cursor proximity to either edge of the ghost block.
      if (index < this.displayItems.length && this.displayItems[index] && this.displayItems[index].isGhostBumper) {
        let blockStart = index
        while (blockStart > 0 && this.displayItems[blockStart - 1] && this.displayItems[blockStart - 1].isGhostBumper) {
          blockStart--
        }
        let blockEnd = index
        while (blockEnd < this.displayItems.length && this.displayItems[blockEnd] && this.displayItems[blockEnd].isGhostBumper) {
          blockEnd++
        }
        const blockMidPx = ((blockStart + blockEnd) / 2) * 65
        index = y < blockMidPx ? blockStart : blockEnd
      }

      this.dropIndex = Math.min(index, this.displayItems.length)

      // Try to extract duration from drag data for optimistic UI preview
      // Note: Some browsers restrict reading dataTransfer during dragover
      try {
        const types = event.dataTransfer.types
        if (types && types.includes('application/json')) {
          // Can't read data during dragover in most browsers, but we set it on dragstart
          // Duration should be set via onExternalDragStart or onItemDragStart
        }
      } catch (e) {
        // Ignore - browser security restriction
      }
    },

    onDrop(event) {
      event.preventDefault()

      // Block drops when data is stale
      if (this.isStale) {
        this.$buefy.toast.open({
          message: 'Schedule data is out of date. Please refresh before making changes.',
          type: 'is-warning',
          duration: 4000
        })
        this.dropIndex = null
        return
      }

      try {
        const mediaData = JSON.parse(event.dataTransfer.getData('application/json'))

        if (!mediaData || this.dropIndex === null) {
          return
        }

        // Resolve real (non-ghost) prev/next items. Issue 43 ghost bumper rows
        // in displayItems have playout-clock times (visual), not DB times —
        // backend insertion math operates on DB time, so skip past them in both
        // directions before computing insertTime or running validation.
        let backIdx = this.dropIndex - 1
        while (backIdx >= 0 && this.displayItems[backIdx].isGhostBumper) backIdx--
        const realPrevItem = backIdx >= 0 ? this.displayItems[backIdx] : null

        let fwdIdx = this.dropIndex
        while (fwdIdx < this.displayItems.length && this.displayItems[fwdIdx].isGhostBumper) fwdIdx++
        const realNextItem = fwdIdx < this.displayItems.length ? this.displayItems[fwdIdx] : null

        // Calculate the insertion time from the resolved real prev item (DB time)
        let insertTime
        if (!realPrevItem) {
          // Dropping at the beginning - use 00:00 of current day
          insertTime = new Date(this.currentDate)
          insertTime.setHours(0, 0, 0, 0)
        } else if (realPrevItem.end && !realPrevItem.isPlaceholder) {
          insertTime = new Date(realPrevItem.end)
        } else if (realPrevItem.start) {
          insertTime = new Date(realPrevItem.start)
        } else {
          insertTime = new Date()
        }

        console.log('Dropping media at index:', this.dropIndex, 'time:', insertTime, 'realPrev:', realPrevItem && realPrevItem.title, 'realNext:', realNextItem && realNextItem.title)

        // VALIDATION 1: Check if trying to drop between segment/break of the same show
        // Shortform can ONLY be inserted:
        //   - In the available time gap BETWEEN two different shows (before next show's Seg 1)
        // Shortform CANNOT be inserted:
        //   - Between segment and its commercial break (same show)
        //   - Between commercial break and the next segment (same show)
        // Uses realPrevItem/realNextItem resolved above (walking past Issue 43 ghost
        // bumper rows) — otherwise validation silently bypasses on ghost neighbours.
        if (realPrevItem && realNextItem) {
          const prevItem = realPrevItem
          const nextItem = realNextItem

          // Day view API (api/dayview.js:120) collapses child rows so that
          // schedule_id == parent's schedule_id for all segments/breaks of a
          // show, and parent_schedule_id is dropped from the response entirely.
          // Week view / raw schedule data keeps parent_schedule_id populated.
          // To detect "child of show X" in both shapes, look at item_type:
          //   - segment/break → child; the row's "parent id" lives in
          //     parent_schedule_id (raw data) or schedule_id (day view collapse)
          //   - everything else → standalone item, no parent.
          const isChildItem = (it) => it.item_type === 'segment' || it.item_type === 'break'
          const parentIdOf = (it) => isChildItem(it)
            ? (it.parent_schedule_id || it.schedule_id)
            : null
          const prevParentId = parentIdOf(prevItem)
          const nextParentId = parentIdOf(nextItem)

          // Skip gap indicators in validation (gaps only appear between different shows)
          if (!prevItem.isGap && !nextItem.isGap && !prevItem.isPlaceholder && !nextItem.isPlaceholder) {
            // CRITICAL CHECK: Specifically block segment -> break insertion (same parent)
            // This is the most important rule to enforce
            if (prevItem.item_type === 'segment' &&
                nextItem.item_type === 'break' &&
                prevParentId &&
                nextParentId &&
                prevParentId === nextParentId) {
              // EXCEPTION (Issue 18b): locked-tail commercial — seam is AVAILABLE TIME
              // for shortform if it fits without pushing the locked break.
              if (nextItem.is_locked) {
                const rawGapSec = (new Date(nextItem.start) - new Date(prevItem.end)) / 1000
                // Deduct the auto Bumper-Out that plays after the segment.
                // (Break has no leading bumper in the export, so no second deduction.)
                const bc = this.bumperConfig
                const bumperOverhead = (bc.bumperEnabled && prevItem.item_type === 'segment')
                  ? bc.bumperDuration : 0
                const gapSec = rawGapSec - bumperOverhead
                const dropSec = mediaData.duration || 0
                if (dropSec <= gapSec) {
                  console.log(`[BroadcastTimeline] ALLOWED into locked-tail AVAILABLE TIME (raw=${rawGapSec}s, bumper=${bumperOverhead}s, effective=${gapSec}s, drop=${dropSec}s)`)
                  // fall through past this block — other validations still apply
                } else {
                  const msg = bumperOverhead > 0
                    ? `Only ${Math.floor(gapSec)}s available before locked commercial (${rawGapSec}s − ${bumperOverhead}s auto-bumper). Drop ${Math.ceil(dropSec)}s is too long.`
                    : `Only ${Math.floor(gapSec)}s available before the locked commercial. Drop ${Math.ceil(dropSec)}s is too long.`
                  this.$buefy.toast.open({
                    message: msg,
                    type: 'is-danger',
                    duration: 4000
                  })
                  this.dropIndex = null
                  return
                }
              } else {
                console.log('[BroadcastTimeline] BLOCKED: Segment-to-break insertion', {
                  prevItem: { id: prevItem.schedule_id, type: prevItem.item_type, segment: prevItem.segment_number, parent: prevItem.parent_schedule_id },
                  nextItem: { id: nextItem.schedule_id, type: nextItem.item_type, parent: nextItem.parent_schedule_id }
                })
                this.$buefy.toast.open({
                  message: 'Cannot insert between a segment and its commercial break. Drop after the break instead.',
                  type: 'is-danger',
                  duration: 4000
                })
                this.dropIndex = null
                return
              }
            }

            // Also check: break -> segment of same show (between break and next segment)
            if (prevItem.item_type === 'break' &&
                nextItem.item_type === 'segment' &&
                prevParentId &&
                nextParentId &&
                prevParentId === nextParentId) {
              console.log('[BroadcastTimeline] BLOCKED: Break-to-segment insertion', {
                prevItem: { id: prevItem.schedule_id, type: prevItem.item_type, parent: prevItem.parent_schedule_id },
                nextItem: { id: nextItem.schedule_id, type: nextItem.item_type, segment: nextItem.segment_number, parent: nextItem.parent_schedule_id }
              })
              this.$buefy.toast.open({
                message: 'Cannot insert between a commercial break and its next segment. Drop after all segments of the show.',
                type: 'is-danger',
                duration: 4000
              })
              this.dropIndex = null
              return
            }

            // Note: segment -> segment of the same show is INTENTIONALLY allowed.
            // The channel template decides where commercial breaks go; segments not
            // separated by a break in the template are back-to-back content with no
            // ad-opt-out concern, so shortform can be inserted between them.
            // The two checks above (segment->break / break->segment) catch the actual
            // protected ad-opt-out positions — that's the only structural rule that
            // needs enforcing at the segment-pair level.
          }
        }

        // VALIDATION 2: Check if media duration fits available time before next locked show (Seg 1)
        // Find the next locked segment 1 after the insertion point
        const lockedShowStarts = this.items.filter(item =>
          item.item_type === 'segment' && item.segment_number === 1
        ).sort((a, b) => new Date(a.start) - new Date(b.start))

        const nextLockedShow = lockedShowStarts.find(locked =>
          new Date(locked.start) > new Date(insertTime)
        )

        if (nextLockedShow) {
          const availableSeconds = (new Date(nextLockedShow.start) - new Date(insertTime)) / 1000
          const mediaDuration = mediaData.duration || 0

          if (mediaDuration > availableSeconds) {
            const formatTime = (seconds) => {
              const mins = Math.floor(seconds / 60)
              const secs = Math.floor(seconds % 60)
              return `${mins}:${String(secs).padStart(2, '0')}`
            }

            this.$buefy.toast.open({
              message: `Item you want to add is too long for available time (${formatTime(mediaDuration)} vs ${formatTime(availableSeconds)} available)`,
              type: 'is-danger',
              duration: 5000
            })
            this.dropIndex = null
            return
          }
        }

        // Validations passed - proceed with drop
        // Check if this is a repositioning operation or new insertion
        if (mediaData.isRepositioning) {
          // Repositioning existing item
          this.$emit('media-reposition', {
            schedule_id: mediaData.schedule_id,
            media_id: mediaData.media_id,
            currentStart: mediaData.currentStart,
            currentEnd: mediaData.currentEnd,
            newStartTime: insertTime,
            duration: mediaData.duration,
            insertIndex: this.dropIndex
          })
        } else {
          // New media insertion from library
          this.$emit('media-drop', {
            media: mediaData,
            insertTime: insertTime,
            insertIndex: this.dropIndex
          })
        }

        this.dropIndex = null
      } catch (error) {
        console.error('Failed to handle drop:', error)
        this.dropIndex = null
      }
    },

    onItemDragStart(event, item) {
      this.draggingItem = item
      this.isDraggingInternal = true
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('application/json', JSON.stringify({
        isRepositioning: true,
        schedule_id: item.schedule_id,
        media_id: item.media_id,
        title: item.title,
        duration: item.duration_seconds,
        media_type: item.media_type,
        currentStart: item.start,
        currentEnd: item.end
      }))
      event.target.classList.add('dragging')
    },

    onItemDragEnd(event) {
      this.draggingItem = null
      this.isDraggingInternal = false
      this.dropIndex = null
      event.target.classList.remove('dragging')
    },

    getMediaIcon(type) {
      const icons = {
        tvc: '📺',
        promo: '📢',
        filler: '🎬',
        gfx: '🎨'
      }
      return icons[type] || '📹'
    },

    calculateGaps() {
      if (!this.items || this.items.length === 0) {
        this.gaps = []
        return
      }

      const gaps = []
      const sortedItems = [...this.items].sort((a, b) => new Date(a.start) - new Date(b.start))

      if (sortedItems.length === 0) {
        this.gaps = []
        return
      }

      // Get day start and end
      const firstItem = sortedItems[0]
      const lastItem = sortedItems[sortedItems.length - 1]
      const dayStart = new Date(firstItem.start)
      dayStart.setHours(0, 0, 0, 0)
      const dayEnd = new Date(dayStart)
      dayEnd.setHours(23, 59, 59, 999)

      // Gap at start of day
      const firstStart = new Date(firstItem.start)
      const startGapSeconds = (firstStart - dayStart) / 1000
      if (startGapSeconds >= 30) {
        gaps.push({
          start: dayStart,
          end: firstStart,
          durationSeconds: startGapSeconds
        })
      }

      // Gaps between items
      for (let i = 0; i < sortedItems.length - 1; i++) {
        const currentEnd = new Date(sortedItems[i].end)
        const nextStart = new Date(sortedItems[i + 1].start)
        const gapSeconds = (nextStart - currentEnd) / 1000

        if (gapSeconds >= 30) {
          gaps.push({
            start: currentEnd,
            end: nextStart,
            durationSeconds: gapSeconds
          })
        }
      }

      // Gap at end of day
      const lastEnd = new Date(lastItem.end)
      const endGapSeconds = (dayEnd - lastEnd) / 1000
      if (endGapSeconds >= 30) {
        gaps.push({
          start: lastEnd,
          end: dayEnd,
          durationSeconds: endGapSeconds
        })
      }

      this.gaps = gaps
    },

    getGapStyle(gap) {
      const dayStart = new Date(gap.start)
      dayStart.setHours(0, 0, 0, 0)

      const startOffsetSeconds = (gap.start - dayStart) / 1000
      const startOffsetHours = startOffsetSeconds / 3600
      const durationHours = gap.durationSeconds / 3600

      const top = startOffsetHours * this.pixelsPerHour * this.zoomLevel
      const height = durationHours * this.pixelsPerHour * this.zoomLevel

      // Add spacing offset to account for fixed-height items (60px)
      const itemHeight = 60
      const spacing = 5 // Gap between items

      return {
        top: `${top + itemHeight + spacing}px`,
        height: `${Math.max(height - itemHeight - (spacing * 2), 50)}px`
      }
    },

    handleDrop(event, gap) {
      try {
        const mediaData = JSON.parse(event.dataTransfer.getData('application/json'))

        console.log('BroadcastTimeline handleDrop - mediaData:', mediaData)
        console.log('BroadcastTimeline handleDrop - gap:', gap)

        if (mediaData.duration > gap.durationSeconds) {
          this.$buefy.toast.open({
            message: `Media duration (${this.formatDuration(mediaData.duration)}) exceeds gap (${this.formatDuration(gap.durationSeconds)})`,
            type: 'is-danger',
            duration: 4000
          })
          return
        }

        this.$emit('media-drop', {
          media: mediaData,
          gap: gap,
          insertTime: gap.start
        })
      } catch (error) {
        console.error('Drop failed:', error)
      }
    },

    handleScroll() {
      // Could add virtual scrolling here if needed
    },

    previousDay() {
      const newDate = new Date(this.currentDate)
      newDate.setDate(newDate.getDate() - 1)
      this.$emit('date-change', newDate)
    },

    nextDay() {
      const newDate = new Date(this.currentDate)
      newDate.setDate(newDate.getDate() + 1)
      this.$emit('date-change', newDate)
    },

    deleteItem(item) {
      this.$emit('item-delete', item)
    },

    // Multi-select methods
    isItemSelected(item) {
      return this.selectedItemIds.includes(item.schedule_id)
    },

    toggleItemSelection(item, checked) {
      if (checked) {
        if (!this.selectedItemIds.includes(item.schedule_id)) {
          this.selectedItemIds.push(item.schedule_id)
        }
      } else {
        this.selectedItemIds = this.selectedItemIds.filter(id => id !== item.schedule_id)
      }
    },

    clearSelection() {
      this.selectedItemIds = []
      this.replaceMode = false
    },

    startReplaceMode() {
      this.replaceMode = true
      // Emit event to notify parent that we're in replace mode
      this.$emit('replace-mode-start', this.selectedItems)
    },

    cancelReplaceMode() {
      this.replaceMode = false
    },

    // Called from parent when replacement media is selected
    confirmReplace(newMedia) {
      if (!this.replaceMode || this.selectedItems.length === 0) return

      this.$buefy.dialog.confirm({
        title: 'Replace Selected Items',
        message: `Replace <b>${this.selectedItems.length}</b> selected item${this.selectedItems.length > 1 ? 's' : ''} with "<b>${newMedia.title}</b>"?`,
        confirmText: 'Replace',
        type: 'is-info',
        hasIcon: true,
        onConfirm: () => {
          this.$emit('items-bulk-replace', {
            itemsToReplace: this.selectedItems,
            newMedia: newMedia
          })
          this.selectedItemIds = []
          this.replaceMode = false
        },
        onCancel: () => {
          this.replaceMode = false
        }
      })
    },

    deleteSelectedItems() {
      if (this.selectedItems.length === 0) return

      this.$buefy.dialog.confirm({
        title: 'Delete Selected Items',
        message: `Are you sure you want to delete <b>${this.selectedItems.length}</b> selected item${this.selectedItems.length > 1 ? 's' : ''}?`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => {
          // Emit bulk delete event with all selected items
          this.$emit('items-bulk-delete', this.selectedItems)
          this.selectedItemIds = []
        }
      })
    },

    // Advanced selection methods
    showSelectByAssetModal() {
      this.isSelectByAssetModalActive = true
    },

    showSelectByTimeRangeModal() {
      // Default to prime time range
      const today = new Date(this.currentDate)
      this.timeRangeStart = new Date(today.setHours(18, 0, 0, 0))
      this.timeRangeEnd = new Date(today.setHours(21, 0, 0, 0))
      this.isSelectByTimeRangeModalActive = true
    },

    selectAllInstancesOf(mediaId) {
      // Select all instances of a specific media asset
      const instances = this.shortformItems.filter(item => item.media_id === mediaId)
      instances.forEach(item => {
        if (!this.selectedItemIds.includes(item.schedule_id)) {
          this.selectedItemIds.push(item.schedule_id)
        }
      })
      this.isSelectByAssetModalActive = false

      this.$buefy.toast.open({
        message: `Selected ${instances.length} instance${instances.length > 1 ? 's' : ''}`,
        type: 'is-success'
      })
    },

    selectAllShortform() {
      // Select all shortform items
      this.shortformItems.forEach(item => {
        if (!this.selectedItemIds.includes(item.schedule_id)) {
          this.selectedItemIds.push(item.schedule_id)
        }
      })

      this.$buefy.toast.open({
        message: `Selected ${this.shortformItems.length} shortform items`,
        type: 'is-success'
      })
    },

    setTimeRange(startHour, startMin, endHour, endMin) {
      const today = new Date(this.currentDate)
      this.timeRangeStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), startHour, startMin, 0)
      this.timeRangeEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), endHour, endMin, 0)
    },

    getItemsInTimeRange() {
      if (!this.timeRangeStart || !this.timeRangeEnd) return []

      const startMinutes = this.timeRangeStart.getHours() * 60 + this.timeRangeStart.getMinutes()
      const endMinutes = this.timeRangeEnd.getHours() * 60 + this.timeRangeEnd.getMinutes()

      return this.shortformItems.filter(item => {
        if (!item.start_time) return false
        const itemDate = new Date(item.start_time)
        const itemMinutes = itemDate.getHours() * 60 + itemDate.getMinutes()
        return itemMinutes >= startMinutes && itemMinutes < endMinutes
      })
    },

    getTimeRangePreviewCount() {
      return this.getItemsInTimeRange().length
    },

    selectByTimeRange() {
      const itemsInRange = this.getItemsInTimeRange()

      itemsInRange.forEach(item => {
        if (!this.selectedItemIds.includes(item.schedule_id)) {
          this.selectedItemIds.push(item.schedule_id)
        }
      })

      this.isSelectByTimeRangeModalActive = false

      this.$buefy.toast.open({
        message: `Selected ${itemsInRange.length} items in time range`,
        type: 'is-success'
      })
    },

    // Copy Day methods
    showCopyDayModal() {
      // Default to same day next week
      const nextWeek = new Date(this.currentDate)
      nextWeek.setDate(nextWeek.getDate() + 7)
      this.copyDestinationDate = nextWeek
      this.isCopyDayModalActive = true
    },

    setCopyDate(daysAhead) {
      const newDate = new Date(this.currentDate)
      newDate.setDate(newDate.getDate() + daysAhead)
      this.copyDestinationDate = newDate
    },

    executeCopyDay() {
      if (!this.copyDestinationDate) return

      this.copyingDay = true

      // Format dates as YYYY-MM-DD in LOCAL timezone (not UTC)
      // toISOString() converts to UTC which shifts the date back for UTC+ timezones
      const formatLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const destDateStr = formatLocal(this.copyDestinationDate)
      const sourceDateStr = formatLocal(this.currentDate)

      // Emit event to parent (EditSchedule.vue) with source items and destination date
      // The parent will call the backend API with channel context
      this.$emit('copy-day-shortform', {
        sourceDate: sourceDateStr,
        destinationDate: destDateStr,
        shortformItems: this.shortformItems.map(item => ({
          schedule_id: item.schedule_id,
          media_id: item.media_id,
          title: item.title,
          duration_seconds: item.duration_seconds,
          media_type: item.media_type,
          start: item.start,
          end: item.end,
          parent_schedule_id: item.parent_schedule_id
        }))
      })

      // The parent will handle the actual API call and close the modal via callback
      // Don't close modal here - wait for parent to signal success/failure
    },

    // Called by parent when copy operation completes
    onCopyDayComplete(success, message) {
      this.copyingDay = false
      if (success) {
        this.isCopyDayModalActive = false
        this.$buefy.toast.open({
          message: message || 'Shortform playlist copied successfully',
          type: 'is-success',
          duration: 4000
        })
      } else {
        this.$buefy.toast.open({
          message: message || 'Failed to copy playlist',
          type: 'is-danger',
          duration: 5000
        })
      }
    }
  }
}
</script>

<style scoped>
.broadcast-timeline-vertical {
  background: white;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid #dbdbdb;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.timeline-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f5f5f5;
  border-bottom: 1px solid #dbdbdb;
  flex-shrink: 0;
}

.date-navigation {
  display: flex;
  align-items: center;
  gap: 12px;
}

.date-display {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: #363636;
  padding: 0 12px;
}

.date-value {
  color: #2196f3;
}

.info-display {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-label {
  font-size: 13px;
  color: #7a7a7a;
}

.info-value {
  font-size: 13px;
  font-weight: 600;
  color: #363636;
}

.selection-divider {
  color: #dbdbdb;
  margin: 0 8px;
}

.selection-count {
  font-size: 13px;
  font-weight: 600;
  color: #3273dc;
  margin-right: 10px;
}

.item-checkbox {
  margin-right: 8px;
  flex-shrink: 0;
}

.timeline-container {
  position: relative;
  flex: 1;
  min-height: 0; /* Allow shrinking below content height so overflow scroll engages */
  overflow-y: auto;
  overflow-x: auto;
  background: #fafafa;
}

.timeline-content {
  position: relative;
  min-width: 100%;
  display: flex;
}

.time-axis {
  position: sticky;
  left: 0;
  width: 70px;
  background: white;
  border-right: 2px solid #dbdbdb;
  z-index: 10;
  flex-shrink: 0;
}

.time-marker {
  position: absolute;
  left: 0;
  width: 100%;
  height: 1px;
}

.time-label {
  font-size: 12px;
  font-weight: 600;
  color: #363636;
  padding: 4px 8px;
  background: white;
  position: relative;
  top: -10px;
}

/* Optimistic preview during drag - shows estimated time shift */
.time-label.preview-time {
  color: #3273dc;
  font-style: italic;
  background: #e8f4fd;
  border: 1px dashed #3273dc;
}

.time-shifted {
  opacity: 0.85;
}

.time-line {
  position: absolute;
  left: 70px;
  top: 0;
  width: 3000px;
  height: 1px;
  background: #dbdbdb;
}

.timeline-track {
  position: relative;
  flex: 1;
  min-width: 0;
}

.timeline-item {
  position: absolute;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;
  overflow: hidden;
}

.timeline-item:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.timeline-item.dragging {
  opacity: 0.5;
  cursor: grabbing;
}

.item-content {
  padding: 8px 12px;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  position: relative;
}

.delete-item-btn {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 59, 48, 0.9);
  color: white;
  font-size: 20px;
  line-height: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  opacity: 0.7;
  font-weight: bold;
  padding: 0;
}

.delete-item-btn:hover {
  opacity: 1;
  background: rgb(255, 59, 48);
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 2px 8px rgba(255, 59, 48, 0.4);
}

.item-title {
  font-weight: 600;
  font-size: 13px;
  line-height: 1.3;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
}

/* TX-readiness dot for shortform items in day view. Matches the week-view
   episode dot in EditSchedule.vue (.tx-status-dot) — same colors, just
   inline rather than absolute-positioned. */
.tx-status-dot-inline {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 1px solid rgba(0, 0, 0, 0.3);
  margin-right: 4px;
  vertical-align: middle;
}
.tx-status-dot-inline.tx-ready {
  background-color: #22c55e;
}
.tx-status-dot-inline.tx-missing {
  background-color: #ef4444;
}

.item-meta {
  font-size: 11px;
  opacity: 0.9;
  white-space: nowrap;
}

.item-duration {
  font-size: 11px;
  opacity: 0.8;
  white-space: nowrap;
  margin-left: auto;
}

/* Item type colors */
.item-segment {
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  color: #1565c0;
}

.item-break {
  background: #ffebee;
  border-left: 6px solid #f44336;
  color: #c62828;
}

.item-break .item-title {
  font-style: italic;
  font-weight: 600;
}

.item-tvc {
  background: #fff3e0;
  border-left: 4px solid #ff9800;
  color: #e65100;
}

.item-promo {
  background: #f3e5f5;
  border-left: 4px solid #9c27b0;
  color: #6a1b9a;
}

.item-filler {
  background: #f1f8e9;
  border-left: 4px solid #8bc34a;
  color: #558b2f;
}

.item-gfx {
  background: #e0f2f1;
  border-left: 4px solid #009688;
  color: #00695c;
}

/* Placeholder items */
.placeholder-content {
  background: transparent !important;
  border: 1px dashed #dbdbdb !important;
}

/* Ghost auto-bumper / PG slate rows — channel-config driven, not in DB.
   Visually distinct so operators see "this 6s is auto-inserted, can't edit". */
.item-ghost-bumper {
  background: repeating-linear-gradient(
    45deg,
    #f5f5f5,
    #f5f5f5 4px,
    #eeeeee 4px,
    #eeeeee 8px
  ) !important;
  border: 1px dashed #9e9e9e !important;
  border-radius: 4px;
  opacity: 0.85;
  cursor: not-allowed;
}
.item-ghost-pgslate {
  border-left: 4px solid #ffc107 !important;
}
.item-ghost-in, .item-ghost-out {
  border-left: 4px solid #607d8b !important;
}
.ghost-bumper-content {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  font-size: 12px;
  color: #555;
}
.ghost-icon {
  font-size: 16px;
}
.ghost-title {
  font-weight: 600;
}
.ghost-meta {
  font-style: italic;
  color: #888;
  margin-left: auto;
}

/* Gap indicators */
.item-gap {
  background: #fff9e6 !important;
  border: 2px dashed #ffc107 !important;
  border-radius: 6px;
}

.gap-content {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
  text-align: center;
}

.gap-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.gap-label {
  font-size: 11px;
  font-weight: 600;
  color: #f57c00;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.gap-duration {
  font-size: 14px;
  font-weight: 700;
  color: #e65100;
}

.gap-next-show {
  font-size: 10px;
  color: #7a7a7a;
  font-style: italic;
  margin-top: 2px;
}

.next-show-title {
  font-weight: 600;
  color: #363636;
}

/* Overrun indicators (red styling) */
.item-overrun {
  background: #ffebee !important;
  border: 2px dashed #f44336 !important;
  border-radius: 6px;
}

.overrun-label {
  color: #c62828 !important;
  font-size: 12px !important;
  font-weight: 700 !important;
}

.overrun-duration {
  color: #b71c1c !important;
  font-size: 16px !important;
  font-weight: 800 !important;
}

.overrun-warning {
  font-size: 10px;
  color: #d32f2f;
  font-style: italic;
  margin-top: 2px;
}

/* Drop indicator */
.drop-indicator {
  position: absolute;
  left: 70px;
  right: 0;
  height: 4px;
  pointer-events: none;
  z-index: 200;
}

.drop-indicator-line {
  width: 100%;
  height: 4px;
  background: #4caf50;
  box-shadow: 0 0 8px rgba(76, 175, 80, 0.6);
}

.drop-indicator-label {
  position: absolute;
  left: 10px;
  top: -20px;
  background: #4caf50;
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

/* Asset selection modal */
.asset-list {
  max-height: 400px;
  overflow-y: auto;
}

.asset-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #dbdbdb;
  border-radius: 4px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.asset-option:hover {
  background: #f0f7ff;
  border-color: #3273dc;
}

.asset-icon {
  font-size: 20px;
}

.asset-title {
  flex: 1;
  font-weight: 500;
}

.asset-count {
  background: #f5f5f5;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  color: #7a7a7a;
  font-weight: 600;
}

/* Time range modal */
.quick-ranges {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
}

.mb-3 {
  margin-bottom: 1rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

/* Quick date buttons for copy day modal */
.quick-date-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
</style>
